import EditIcon from "@mui/icons-material/Edit";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import {
  Box,
  Dialog,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { Organizations } from "@knowfully/ui.common";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import SnackbarMessage from "components/SnackbarMessage";
import useBoolean from "hooks/useBoolean";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";
import AccreditationChain from "pages/LicensureBodiesPage/AccreditationChain";
import SubmissionAuthorityForm from "./SubmissionAuthorityForm";

const SubmissionAuthorityDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    submissionAuthorityService,
    submissionAuthoritiesUrl,
    submissionAuthoritySkusUrl,
  } = useGlobalContext();
  const { organizationId } = contextStore.useState();
  const [editOpen, editOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const isRootOrganization =
    organizationId === Organizations.KnowfullyLearningGroup;

  const submissionAuthorityResult = useStrictQuery(
    ["submissionAuthorityService.getAuthorityTypeById", id, organizationId],
    () => submissionAuthorityService.getAuthorityTypeById(id!),
    {
      enabled: !!id,
    },
  );

  const submissionAuthority = submissionAuthorityResult.data?.data;
  useTitle(submissionAuthority?.name);

  const onSubmissionAuthorityUpdated = () => {
    queryClient.invalidateQueries([
      "submissionAuthorityService.getAuthorityTypeById",
    ]);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "submissionAuthorityUpdated" })),
    );
    editOpenActions.setFalse();
  };

  const rows = [
    RowElement(
      "nameLabel",
      submissionAuthority?.name!,
      submissionAuthorityResult.isLoading,
    ),
    RowElement(
      "uploadSubmissionStatusAvailableLabel",
      submissionAuthority?.isUploadSubmissionStatusAvailable
        ? t({ id: "yes" })
        : t({ id: "no" }),
      submissionAuthorityResult.isLoading,
    ),
    RowElement(
      "submissionCutOffDaysLabel",
      submissionAuthority?.submissionCutOffDays ?? "-",
      submissionAuthorityResult.isLoading,
    ),
  ];

  if (!id) return <Navigate to={submissionAuthoritiesUrl()} replace />;
  if (submissionAuthorityResult.isLoading) return <Loader />;

  if (!submissionAuthority) {
    return (
      <>
        <RedirectBackButton
          redirectRoute={submissionAuthoritiesUrl()}
          labelKey="backToListButtonLabel"
        />
        <Alert severity="info">
          {t({ id: "submissionAuthorityNotAccessible" })}
        </Alert>
      </>
    );
  }

  return (
    <>
      <RedirectBackButton
        redirectRoute={submissionAuthoritiesUrl()}
        labelKey="backToListButtonLabel"
      />
      <h1>{submissionAuthority.name}</h1>

      <Box mb={3} display="flex" justifyContent="center" gap={4}>
        <Tooltip title={t({ id: "editSubmissionAuthority" })}>
          <Fab
            color="primary"
            onClick={editOpenActions.setTrue}
            disabled={!isRootOrganization}
          >
            <EditIcon />
          </Fab>
        </Tooltip>
        <Tooltip title={t({ id: "associatedSkus" })}>
          <Fab
            color="primary"
            onClick={() => navigate(submissionAuthoritySkusUrl(id))}
          >
            <LocalLibraryIcon />
          </Fab>
        </Tooltip>
      </Box>

      <Typography variant="h5" sx={{ margin: "20px 0" }}>
        {t({ id: "submissionAuthority" })}
      </Typography>
      <TableContainer>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell component="th" scope="row" width="30%">
                  <b>{title}</b>
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <AccreditationChain items={submissionAuthority.accreditationChain} />

      <Dialog open={editOpen} disableEnforceFocus>
        <SubmissionAuthorityForm
          authorityTypeId={id}
          submissionAuthority={submissionAuthority}
          title={t({ id: "editSubmissionAuthority" })}
          onSubmit={onSubmissionAuthorityUpdated}
          onCancel={editOpenActions.setFalse}
        />
      </Dialog>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default SubmissionAuthorityDetails;
